import axios from 'Adapter';
import { api } from 'Utils';
import { DataQueryParams, Fetcher, JobStatisticReportRange } from 'Types';

export const fetcher = async (url: string) =>
	await axios.get(url).then((res) => res.data);

export const postFetcher = async (url: string, body?: Record<string, never>) =>
	await axios.post(url, body).then((res) => res.data);

const fetcherParam = async (api_url: string, data: DataQueryParams) => {
	const searchParams = new URLSearchParams(data)?.toString();
	return await fetcher(`${api_url}?${searchParams}`);
};

const postFetcherList = (api_url: string, body: Record<string, unknown>) => {
	return postFetcher(api_url, body as any);
};

export const fetch: Fetcher | any = {
	// Permission Fetcher
	permissions: (localization = 'en') =>
		fetcherParam(api.getPermissions, { localization }),
	permissionsGraph: () => fetcher(api.getPermissionsGraph),

	// User type Fetcher
	businessPartnerProfile: (businessPartnerProfileId) =>
		fetcher(api.getBusinessPartnerProfile(businessPartnerProfileId)),
	communityPartnerProfile: (communityPartnerProfileId) =>
		fetcher(api.getCommunityPartnerProfile(communityPartnerProfileId)),
	plansCommunityPartner: (communityPartnerProfileId) =>
		fetcher(api.getPlansCommunityPartner(communityPartnerProfileId)),
	businessPartnerMe: () => fetcher(api.getBusinessPartnerMe),
	PaymentMethods: () => fetcher(api.getPaymentMethods),
	communityPartnerMe: () => fetcher(api.getCommunityPartnerMe),
	communityPartnerCommissionLevelMe: () =>
		fetcher(api.getCommunityPartnerCommissionLevelMe),

	// Employee Fetcher
	employeeList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getEmployees, { pageSize, pageNumber, keyword }),
	employeeDetails: (id) => fetcher(api.getEmployeeDetail(id)),

	// branch Fetcher
	branchList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getBranches, { pageSize, pageNumber, keyword }),
	branchDetails: (id) => fetcher(api.getBranchDetail(id)),

	// Member Fetcher
	memberList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getMembers, { pageSize, pageNumber, keyword }),
	memberDetails: (id) => fetcher(api.getMemberDetail(id)),

	// Department Fetcher
	departmentList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getDepartments, { pageSize, pageNumber, keyword }),
	departmentDetails: (id) => fetcher(api.getDepartmentDetail(id)),

	// Activity Fetcher
	activityList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getActivities, { pageSize, pageNumber, keyword }),

	// Benefit Fetcher
	benefitList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getBenefits, { pageSize, pageNumber, keyword }),

	// Sector Fetcher
	sectorList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getSectors, { pageSize, pageNumber, keyword }),

	// Job type Fetcher
	jobTypeList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getJobTypes, { pageSize, pageNumber, keyword }),

	// Job Usage
	jobUsage: () => fetcher(api.getJobUsages),
	jobGeneralUsage: () => fetcher(api.getJobGeneralUsages),

	// Job Tag Categories
	jobTagCategories: (pageSize, pageNumber, keyword, kind) =>
		fetcherParam(api.getJobTagCategories, {
			pageSize,
			pageNumber,
			keyword,
			kind
		}),

	// Job Google Analytics
	jobInsight: (body) => postFetcherList(api.jobInsight, body),
	bpProfileInsight: (body) =>
		postFetcherList(api.businessPartnerProfileInsight, body),
	cpProfileInsight: (body) =>
		postFetcherList(api.communityPartnerProfileInsight, body),

	// Activity Tags
	activityTags: (pageSize, pageNumber, keyword, isActive) =>
		fetcherParam(api.getActivityTags, {
			pageSize,
			pageNumber,
			keyword,
			isActive
		}),

	// Job Tags
	jobTags: (
		pageSize,
		pageNumber,
		keyword,
		kind,
		jobTagCategoryId,
		noAssignedCategory,
		isActive
	) =>
		fetcherParam(api.getJobTags, {
			pageSize,
			pageNumber,
			keyword,
			kind,
			jobTagCategoryId,
			noAssignedCategory,
			isActive
		}),

	// Job Fetcher
	profileJobList: (status, pageSize, pageNumber, keyword, kind) =>
		fetcherParam(api.getJobs, {
			status,
			pageSize,
			pageNumber,
			keyword,
			kind
		}),
	publishJobs: (reqBody) => postFetcherList(api.getPublishJobs, reqBody),
	searchWithAnalyticsJobs: (reqBody) =>
		postFetcherList(api.getSearchWithAnalyticsJobs, reqBody),
	jobDetails: (id) => fetcher(api.getJobDetail(id)),
	jobQrCode: (jobId) => fetcher(api.getJobQrCode(jobId)),
	downloadJobQrCode: (jobId) => fetcher(api.downloadJobQrCode(jobId)),

	// User Fetcher
	userList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getB2bUsers, { pageSize, pageNumber, keyword }),
	userDetails: (id) => fetcher(api.getUserDetail(id)),

	// Partner Fetcher
	partnersInsight: () => postFetcherList(api.getPartnerInsights, {}),
	communityPartnerPartners: (
		pageSize,
		pageNumber,
		keyword,
		status,
		registrationStatus,
		publishStatus,
		subscriptionPlanId
	) =>
		fetcherParam(api.getCommunityPartnerPartners, {
			pageSize,
			pageNumber,
			keyword,
			status,
			registrationStatus,
			publishStatus,
			subscriptionPlanId
		}),
	communityPartners: (
		pageSize,
		pageNumber,
		keyword,
		regionCode,
		IsPremium,
		published
	) =>
		fetcherParam(api.getCommunityPartnersCompanies, {
			pageSize,
			pageNumber,
			keyword,
			regionCode,
			IsPremium,
			published
		}),
	communityPartnerAreaList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getCommunityPartnerArea, {
			pageSize,
			pageNumber,
			keyword
		}),
	// Subscription Fetcher
	subscriptionPlanList: (pageSize, pageNumber, keyword, planType) =>
		fetcherParam(api.getSubscriptionPlans, {
			planType,
			pageSize,
			pageNumber,
			keyword
		}),
	subscriptionPlanDetails: (subscriptionPlanId) =>
		fetcher(api.getSubscriptionPlanDetails(subscriptionPlanId)),

	companySubscriptionList: (
		pageSize,
		pageNumber,
		keyword,
		subscriptionPlanType,
		subscriptionStatus
	) =>
		fetcherParam(api.getCompanySubscriptions, {
			pageSize,
			pageNumber,
			keyword,
			subscriptionPlanType,
			subscriptionStatus
		}),
	subscriptionCalculate: (
		CommunityPartnerId,
		SubscriptionPlanId,
		VoucherCode,
		SubscriptionPlanType
	) =>
		fetcher(
			api.getSubscriptionCalculate(
				CommunityPartnerId,
				SubscriptionPlanId,
				VoucherCode,
				SubscriptionPlanType
			)
		),
	subscriptionCalculateUpgrade: (
		CommunityPartnerId,
		CurrentSubscriptionPlanId,
		UpgradeSubscriptionPlanId,
		VoucherCode,
		SubscriptionPlanType
	) =>
		fetcher(
			api.getSubscriptionCalculateUpgrade(
				CommunityPartnerId,
				CurrentSubscriptionPlanId,
				UpgradeSubscriptionPlanId,
				VoucherCode,
				SubscriptionPlanType
			)
		),
	subscriptionPlansFeature: (subscriptionPlanFeatureId, SubscriptionPlanId) =>
		fetcher(
			api.getSubscriptionPlansFeature(
				subscriptionPlanFeatureId,
				SubscriptionPlanId
			)
		),
	subscriptionPlansCommissionGroups: (subscriptionPlanFeatureId) =>
		fetcher(
			api.getSubscriptionPlansCommissionGroups(subscriptionPlanFeatureId)
		),
	subscriptionDetails: (subscriptionId) =>
		fetcher(api.getSubscriptionDetail(subscriptionId)),
	subscriptionPaymentsDetails: (currentSubscriptionPaymentId) =>
		fetcher(
			api.getSubscriptionPaymentsDetail(currentSubscriptionPaymentId)
		),

	businessPartnerInvoiceDetails: (invoiceId) =>
		fetcher(api.getBusinessPartnerInvoiceDetail(invoiceId)),

	subscriptionPayments: (subscriptionId, pageNumber, pageSize) =>
		fetcher(
			api.getSubscriptionPayments(subscriptionId, pageNumber, pageSize)
		),

	// Demands Fetcher
	demandList: (status, pageSize, pageNumber, keyword) =>
		fetcherParam(api.getDemands, { status, pageSize, pageNumber, keyword }),
	demandDetails: (demandId, applicantId) =>
		fetcher(api.getDemandDetail(demandId, applicantId)),

	// Region Fetcher
	regionsList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getRegions, {
			pageSize,
			pageNumber,
			keyword
		}),

	// Credits Fetcher
	communityPartnerCreditList: (
		communityPartnerCreditStatus,
		pageSize,
		pageNumber,
		keyword
	) =>
		fetcherParam(api.getCommunityPartnerCredits, {
			communityPartnerCreditStatus,
			pageSize,
			pageNumber,
			keyword
		}),
	communityPartnerCreditPayment: (
		pageSize,
		pageNumber,
		keyword,
		status,
		minTotalPaymentAmount,
		maxTotalPaymentAmount,
		minTotalDonatedAmount,
		maxTotalDonatedAmount,
		year,
		month
	) =>
		fetcherParam(api.getCommunityPartnerCreditPayments, {
			pageSize,
			pageNumber,
			keyword,
			status,
			minTotalPaymentAmount,
			maxTotalPaymentAmount,
			minTotalDonatedAmount,
			maxTotalDonatedAmount,
			year,
			month
		}),
	communityPartnerPaymentList: (
		subscriptionId,
		pageSize,
		pageNumber,
		keyword
	) =>
		fetcherParam(api.getCommunityPartnerPayments, {
			subscriptionId,
			pageSize,
			pageNumber,
			keyword
		}),
	communityPartnerInvoiceList: (
		subscriptionId,
		reason,
		pageSize,
		pageNumber,
		keyword,
		status
	) =>
		fetcherParam(api.getBusinessPartnerInvoice, {
			subscriptionId,
			reason,
			pageSize,
			pageNumber,
			keyword,
			status
		}),
	communityPartnerCreditDetails: (creditId) =>
		fetcher(api.getCommunityPartnerCreditDetails(creditId)),
	communityPartnerPaymentDetails: (creditpaymentId) =>
		fetcher(api.getCommunityPartnerPaymentDetails(creditpaymentId)),
	paymentStatus: (paymentId) => fetcher(api.getPaymentStatus(paymentId)),

	// Voucher Fetcher
	communityPartnerVouchers: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getCommunityPartnerVouchers, {
			pageSize,
			pageNumber,
			keyword
		}),
	availableVouchers: () => fetcher(api.getAvailableVouchers),
	communityPartnerTypes: (pageSize, pageNumber, keyword, active) =>
		fetcherParam(api.getCommunityPartnerTypes, {
			pageSize,
			pageNumber,
			keyword,
			active
		}),
	communityPartnerAssociation: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getCommunityPartnerAssociation, {
			pageSize,
			pageNumber,
			keyword
		}),
	CommunityPartnerVoucherDetails: (voucherId) =>
		fetcher(api.getCommunityPartnerVoucherDetails(voucherId)),

	// Invitation Fetcher
	invitationList: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getInvitations, {
			pageSize,
			pageNumber,
			keyword
		}),
	invitationDetails: (invitation) =>
		fetcher(api.getInvitationDetails(invitation)),

	// Event Fetcher

	/** Agent event fetcher */
	eventList: (
		status,
		pageSize,
		pageNumber,
		keyword,
		communityPartnerIsActive
	) =>
		fetcherParam(api.getEvents, {
			status,
			pageSize,
			pageNumber,
			keyword,
			communityPartnerIsActive
		}),
	// publishEventList: (isFinished, pageSize, pageNumber) =>
	// 	fetcherParam(api.getPublicEvents, {
	// 		isFinished,
	// 		pageSize,
	// 		pageNumber
	// 	}),
	eventDetails: (eventId) => fetcher(api.getEvent(eventId)),
	// eventInvitations: (eventId, pageSize, pageNumber) =>
	// 	fetcherParam(api.getEventInvitations(eventId), {
	// 		pageSize,
	// 		pageNumber
	// 	}),
	eventAllInvitations: (eventId, isPartner, status, keyword) =>
		fetcher(
			api.getEventAllInvitations(eventId, isPartner, status, keyword)
		),
	companiesToInvite: (pageSize, pageNumber, keyword) =>
		fetcherParam(api.getCompaniesToInvite, {
			pageSize,
			pageNumber,
			keyword
		}),
	participantList: (
		eventId,
		isPartner,
		status,
		pageSize,
		pageNumber,
		keyword
	) =>
		fetcherParam(api.getParticipants(eventId), {
			isPartner,
			status,
			pageSize,
			pageNumber,
			keyword
		}),

	/** Company event fetcher */
	companyEventDetails: (eventId) => fetcher(api.getEventDetails(eventId)),
	companyEventLimit: (eventId) => fetcher(api.getEventLimit(eventId)),
	companyEventInvitations: (pageSize, pageNumber, finishedOnly, status) =>
		fetcherParam(api.getCompanyEventInvitations, {
			pageSize,
			pageNumber,
			finishedOnly,
			status
		}),
	companyParticipatedEvent: (pageSize, pageNumber, status) =>
		fetcherParam(api.getCompanyParticipatedEvent, {
			pageSize,
			pageNumber,
			status
		}),

	// Toolbox Fetcher
	marketingAssets: (useFor) =>
		fetcherParam(api.getMarketingAssets, { useFor }),
	marketingTemplates: (toolType, useFor) =>
		fetcherParam(api.getMarketingTemplates, { toolType, useFor }),
	marketingAssetConfig: (assetId) =>
		fetcher(api.getMarketingAssetConfig(assetId)),

	// Job Statistics Fetcher
	jobStatistics: (reportRange = JobStatisticReportRange.Monthly) =>
		fetcherParam(api.getJobStatistic, { reportRange }),
	totalJobStatistic: () => fetcher(api.getTotalJobStatistic),

	//Contact Groups
	listContactGroup: (isActive, pageSize, pageNumber, keyword) =>
		fetcherParam(api.getContactGroups, {
			isActive,
			pageSize,
			pageNumber,
			keyword
		}),
	contactGroupDetails: (contactGroupId) =>
		fetcher(api.getContactGroupDetails(contactGroupId)),

	//Contact People
	listContactPeople: (contactGroupId, pageSize, pageNumber, keyword) =>
		fetcherParam(api.getContactPeople, {
			contactGroupId,
			pageSize,
			pageNumber,
			keyword
		}),
	contactPeopleDetails: (contactPeopleId) =>
		fetcher(api.getContactPeopleDetails(contactPeopleId)),

	partnerDetails: (businessPartnerId) =>
		fetcher(api.getPartnerDetails(businessPartnerId))
};
